import apiService from "../../../service/Api";
import type { CompetencyQuestionTemplate } from "../types";

const API_BASE = "/api/configuration/competency-templates";

type PaginatedResponse<T> = { results: T[]; next: string | null };

function unwrapList<T>(data: T[] | PaginatedResponse<T>): T[] {
  return Array.isArray(data) ? data : data.results;
}

/**
 * Fetch all competency question templates with pagination support
 */
export async function getCompetencyTemplatesFromAPI(): Promise<
  CompetencyQuestionTemplate[]
> {
  try {
    const all: CompetencyQuestionTemplate[] = [];
    let url: string | null = API_BASE + "/";

    while (url) {
      const response: {
        data: CompetencyQuestionTemplate[] | PaginatedResponse<CompetencyQuestionTemplate>;
      } = await apiService.get<
        CompetencyQuestionTemplate[] | PaginatedResponse<CompetencyQuestionTemplate>
      >(url);
      all.push(...unwrapList(response.data));
      url = Array.isArray(response.data)
        ? null
        : (response.data as PaginatedResponse<CompetencyQuestionTemplate>).next;
    }

    return all;
  } catch (error) {
    console.error("Error fetching competency templates:", error);
    throw error;
  }
}

/**
 * Fetch a single competency template by ID
 */
export async function getCompetencyTemplateByIdFromAPI(
  id: string,
): Promise<CompetencyQuestionTemplate> {
  try {
    const response = await apiService.get<CompetencyQuestionTemplate>(
      `${API_BASE}/${id}/`,
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching competency template ${id}:`, error);
    throw error;
  }
}

/**
 * Create a new competency question template
 */
export async function createCompetencyTemplateAPI(
  templateData: Omit<CompetencyQuestionTemplate, "id" | "createdAt">,
): Promise<CompetencyQuestionTemplate> {
  try {
    const payload = {
      name: templateData.name,
      department: templateData.department,
      questions: templateData.questions.map((q) => ({
        question: q.question,
        question_type: q.type, // Convert type to question_type for backend
        points: q.points,
        order: templateData.questions.indexOf(q),
        is_required: true,
        choices: q.type === "single_choice"
          ? (q.options || []).map((text, idx) => ({
              text,
              order: idx,
              is_correct: q.correctOptionIndex === idx,
            }))
          : [],
      })),
    };

    const response = await apiService.post<CompetencyQuestionTemplate>(
      `${API_BASE}/`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error creating competency template:", error);
    throw error;
  }
}

/**
 * Update an existing competency template
 */
export async function updateCompetencyTemplateAPI(
  id: string,
  templateData: Omit<CompetencyQuestionTemplate, "id" | "createdAt">,
): Promise<CompetencyQuestionTemplate> {
  try {
    const payload = {
      name: templateData.name,
      department: templateData.department,
      questions: templateData.questions.map((q) => ({
        question: q.question,
        question_type: q.type,
        points: q.points,
        order: templateData.questions.indexOf(q),
        is_required: true,
        choices: q.type === "single_choice"
          ? (q.options || []).map((text, idx) => ({
              text,
              order: idx,
              is_correct: q.correctOptionIndex === idx,
            }))
          : [],
      })),
    };

    const response = await apiService.put<CompetencyQuestionTemplate>(
      `${API_BASE}/${id}/`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating competency template ${id}:`, error);
    throw error;
  }
}

/**
 * Delete a competency template
 */
export async function deleteCompetencyTemplateAPI(id: string): Promise<void> {
  try {
    await apiService.delete(`${API_BASE}/${id}/`);
  } catch (error) {
    console.error(`Error deleting competency template ${id}:`, error);
    throw error;
  }
}

/**
 * Fetch templates by department
 */
export async function getCompetencyTemplatesByDepartmentAPI(
  department: string,
): Promise<CompetencyQuestionTemplate[]> {
  try {
    const response = await apiService.get<
      CompetencyQuestionTemplate[] | PaginatedResponse<CompetencyQuestionTemplate>
    >(`${API_BASE}/?department=${department}`);
    return unwrapList(response.data);
  } catch (error) {
    console.error(`Error fetching templates for department ${department}:`, error);
    throw error;
  }
}
